import { Bell, Calendar, CalendarPlus, Check, Clock, Hash, MapPin, Pencil, Plus, Trash2, Volume2 } from '@/components/ui/icons';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { APP_NAME, createEventSchema, LIMITS, type PublicUser, type ServerEventDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { eventApi } from '@/api/endpoints';
import { Avatar } from '@/components/ui/Avatar';
import { Button, IconButton } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/Feedback';
import { Input, Select, TextArea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useSubmit, validate } from '@/hooks/useForm';
import { useMyPermissions } from '@/lib/permissions';
import { downloadIcs } from '@/lib/ics';
import { formatEventTime } from '@/lib/time';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { isHappening, sortEvents, useEvents } from '@/stores/events';
import { useServerChannels, useServers } from '@/stores/servers';
import { confirm, toast } from '@/stores/ui';

const monthFmt = new Intl.DateTimeFormat(undefined, { month: 'short' });
const weekdayFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short' });

/** "Happening now", "Starts in 20 min", "Today", "Tomorrow", "In 3 days". */
function whenLabel(event: ServerEventDTO, now = new Date()): { text: string; live: boolean; soon: boolean } {
  if (isHappening(event, now.getTime())) return { text: 'Happening now', live: true, soon: false };
  const start = new Date(event.startsAt);
  const minutes = Math.round((start.getTime() - now.getTime()) / 60_000);
  if (minutes < 60) return { text: `Starts in ${Math.max(1, minutes)} min`, live: false, soon: true };
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const days = Math.round((startDay - today) / (24 * 60 * 60_000));
  if (days <= 0) return { text: 'Today', live: false, soon: true };
  if (days === 1) return { text: 'Tomorrow', live: false, soon: false };
  return { text: `In ${days} days`, live: false, soon: false };
}

/** A server's upcoming events: mark them interested, open where they happen, and (with Manage Events) make and edit them. */
export function EventsModal({ serverId, eventId, startCreating, onClose }: { serverId: string; eventId?: string; startCreating?: boolean; onClose: () => void }) {
  const server = useServers((s) => s.servers[serverId]);
  const loaded = useEvents((s) => !!s.loaded[serverId]);
  const record = useEvents((s) => s.byServer[serverId]);
  const events = useMemo(() => sortEvents(record), [record]);
  const perms = useMyPermissions(serverId);
  const [editing, setEditing] = useState<ServerEventDTO | 'new' | null>(startCreating ? 'new' : null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    useEvents
      .getState()
      .load(serverId)
      .catch((err) => setError(errorMessage(err)));
  }, [serverId]);

  useEffect(() => {
    if (eventId && loaded) document.getElementById(`event-${eventId}`)?.scrollIntoView({ block: 'center' });
  }, [eventId, loaded]);

  if (!server) return null;
  if (editing) return <EventForm serverId={serverId} event={editing === 'new' ? null : editing} onDone={() => setEditing(null)} onClose={onClose} />;

  return (
    <Modal
      open
      onClose={onClose}
      title="Events"
      description={loaded && events.length ? `${events.length} coming up in ${server.name}.` : `What's coming up in ${server.name}.`}
      footer={
        perms.can('MANAGE_EVENTS') ? (
          <Button icon={<Plus size={16} />} onClick={() => setEditing('new')}>
            Create event
          </Button>
        ) : undefined
      }
    >
      {error ? (
        <p className="text-sm text-danger">{error}</p>
      ) : !loaded ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : events.length === 0 ? (
        <EmptyState
          icon={<Calendar size={26} />}
          title="No upcoming events"
          body={perms.can('MANAGE_EVENTS') ? 'Plan a game night, a watch party or a meeting, and members can say they’re interested.' : 'When something’s planned, it shows up here.'}
        />
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {events.map((event) => (
              <EventCard key={event.id} event={event} highlighted={event.id === eventId} canManage={perms.can('MANAGE_EVENTS')} onEdit={() => setEditing(event)} onNavigate={onClose} />
            ))}
          </ul>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-fg-muted">
            <Bell size={13} className="shrink-0" /> Press <strong className="font-semibold text-fg-soft">I’m interested</strong> to get a reminder 15 minutes before it starts.
          </p>
        </>
      )}
    </Modal>
  );
}

function EventCard({ event, highlighted, canManage, onEdit, onNavigate }: { event: ServerEventDTO; highlighted: boolean; canManage: boolean; onEdit: () => void; onNavigate: () => void }) {
  const navigate = useNavigate();
  const myId = useAuth((s) => s.user?.id);
  const serverName = useServers((s) => s.servers[event.serverId]?.name);
  const channel = useServers((s) => (event.channelId ? s.channels[event.channelId] : undefined));
  const [busy, setBusy] = useState(false);
  const [people, setPeople] = useState<PublicUser[] | null>(null);
  const [showPeople, setShowPeople] = useState(false);
  const start = new Date(event.startsAt);
  const when = whenLabel(event);
  const canCancel = canManage || event.createdBy?.id === myId;

  // Who's interested, for the faces next to the count (refreshed when the count changes).
  useEffect(() => {
    if (event.interestedCount === 0) return setPeople([]);
    let cancelled = false;
    eventApi
      .interested(event.serverId, event.id)
      .then((list) => !cancelled && setPeople(list))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [event.serverId, event.id, event.interestedCount]);

  const toggle = async () => {
    setBusy(true);
    try {
      useEvents.getState().upsert(await eventApi.setInterested(event.serverId, event.id, !event.interested));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!(await confirm({ title: `Cancel ${event.name}?`, body: 'It’s removed for everyone.', confirmLabel: 'Cancel event', danger: true }))) return;
    try {
      await eventApi.remove(event.serverId, event.id);
      useEvents.getState().remove(event.serverId, event.id);
      toast.success('Event cancelled.');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const addToCalendar = () => {
    const where = channel ? `${channel.type === 'VOICE' ? '' : '#'}${channel.name} in ${serverName ?? APP_NAME} (${APP_NAME})` : event.location;
    downloadIcs({
      id: event.id,
      name: event.name,
      description: event.description,
      startsAt: event.startsAt,
      endsAt: event.endsAt,
      where,
      url: `${window.location.origin}/app/${event.serverId}${channel ? `/${channel.id}` : ''}`,
    });
  };

  const faces = (people ?? []).slice(0, 4);

  return (
    <li
      id={`event-${event.id}`}
      className={cn('overflow-hidden rounded-2xl border bg-sidebar transition-colors', highlighted ? 'border-accent shadow-[0_0_0_1px_var(--clanfire-accent)]' : 'border-line', when.live && 'border-success/60')}
    >
      <div className="flex gap-4 p-4">
        {/* The date, like a page torn off a calendar. */}
        <div className="flex h-[72px] w-14 shrink-0 flex-col items-center overflow-hidden rounded-xl border border-line bg-surface text-center" aria-hidden>
          <span className={cn('w-full py-0.5 text-[10px] font-bold tracking-wide uppercase', when.live ? 'bg-success text-white' : 'bg-accent text-accent-fg')}>{monthFmt.format(start)}</span>
          <span className="mt-1 font-display text-2xl leading-none text-fg">{start.getDate()}</span>
          <span className="mt-0.5 text-[10px] font-semibold text-fg-muted uppercase">{weekdayFmt.format(start)}</span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <span
                className={cn(
                  'mb-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold',
                  when.live ? 'bg-success/15 text-success' : when.soon ? 'bg-warning/15 text-warning' : 'bg-accent-soft text-accent-ink',
                )}
              >
                {when.live && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" aria-hidden />}
                {when.text}
              </span>
              <h3 className="text-[17px] leading-snug font-bold break-words text-fg">{event.name}</h3>
            </div>
            {(canManage || canCancel) && (
              <div className="-mt-1 -mr-1 flex shrink-0">
                {canManage && (
                  <IconButton label="Edit event" size="sm" onClick={onEdit}>
                    <Pencil size={15} />
                  </IconButton>
                )}
                {canCancel && (
                  <IconButton label="Cancel event" size="sm" tone="danger" onClick={() => void cancel()}>
                    <Trash2 size={15} />
                  </IconButton>
                )}
              </div>
            )}
          </div>

          <ul className="mt-1.5 flex flex-col gap-1 text-sm text-fg-soft">
            <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <Clock size={14} className="shrink-0 text-fg-muted" /> {formatEventTime(event)}
              <button type="button" onClick={addToCalendar} className="inline-flex items-center gap-1 text-xs font-semibold text-link hover:underline">
                <CalendarPlus size={13} /> Add to calendar
              </button>
            </li>
            {channel ? (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    onNavigate();
                    navigate(`/app/${event.serverId}/${channel.id}`);
                  }}
                  className="flex items-center gap-2 font-medium text-link hover:underline"
                >
                  {channel.type === 'VOICE' ? <Volume2 size={14} className="shrink-0" /> : <Hash size={14} className="shrink-0" />} {channel.name}
                </button>
              </li>
            ) : event.location ? (
              <li className="flex items-center gap-2 break-all">
                <MapPin size={14} className="shrink-0 text-fg-muted" /> {event.location}
              </li>
            ) : null}
            {event.createdBy && (
              <li className="flex items-center gap-2 text-fg-muted">
                <Avatar user={event.createdBy} size={16} decorate={false} /> Hosted by {event.createdBy.displayName}
              </li>
            )}
          </ul>

          {event.description && <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap text-fg">{event.description}</p>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-line bg-surface/40 px-4 py-2.5">
        <Button size="sm" variant={event.interested ? 'success' : 'primary'} icon={event.interested ? <Check size={14} /> : <Bell size={14} />} loading={busy} onClick={() => void toggle()} aria-pressed={event.interested}>
          {event.interested ? 'Interested' : 'I’m interested'}
        </Button>
        <button
          type="button"
          onClick={() => setShowPeople((v) => !v)}
          disabled={event.interestedCount === 0}
          className="flex items-center gap-2 rounded-full py-0.5 pr-1 text-sm text-fg-muted enabled:hover:text-fg disabled:cursor-default"
          aria-expanded={showPeople}
        >
          {faces.length > 0 && (
            <span className="flex -space-x-1.5" aria-hidden>
              {faces.map((u) => (
                <Avatar key={u.id} user={u} size={20} decorate={false} className="rounded-full ring-2 ring-sidebar" />
              ))}
            </span>
          )}
          {event.interestedCount === 0 ? 'Nobody interested yet' : `${event.interestedCount} interested`}
        </button>
      </div>

      {showPeople && people && people.length > 0 && (
        <ul className="flex flex-wrap gap-2 border-t border-line px-4 py-3" aria-label="Interested">
          {people.map((u) => (
            <li key={u.id} className="flex items-center gap-1.5 rounded-full bg-sunken py-0.5 pr-2.5 pl-0.5 text-sm">
              <Avatar user={u} size={22} decorate={false} /> {u.displayName}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/* ------------------------------------------------------------------ */

const pad = (n: number) => String(n).padStart(2, '0');
const dateValue = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeValue = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

function EventForm({ serverId, event, onDone, onClose }: { serverId: string; event: ServerEventDTO | null; onDone: () => void; onClose: () => void }) {
  const channels = useServerChannels(serverId).filter((c) => c.type === 'VOICE' || c.type === 'TEXT');
  const initialStart = event ? new Date(event.startsAt) : new Date(Math.ceil((Date.now() + 60 * 60_000) / (30 * 60_000)) * 30 * 60_000);
  const initialEnd = event?.endsAt ? new Date(event.endsAt) : null;
  const [values, setValues] = useState({
    name: event?.name ?? '',
    description: event?.description ?? '',
    date: dateValue(initialStart),
    start: timeValue(initialStart),
    hasEnd: !!initialEnd,
    end: initialEnd ? timeValue(initialEnd) : timeValue(new Date(initialStart.getTime() + 2 * 60 * 60_000)),
    where: event && !event.channelId && event.location ? 'elsewhere' : 'channel',
    channelId: event?.channelId ?? channels.find((c) => c.type === 'VOICE')?.id ?? channels[0]?.id ?? '',
    location: event?.location ?? '',
  });
  const { loading, error, fields, setFields, run } = useSubmit();
  const set = (patch: Partial<typeof values>) => setValues((v) => ({ ...v, ...patch }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const startsAt = new Date(`${values.date}T${values.start}`);
    let endsAt: Date | null = null;
    if (values.hasEnd) {
      endsAt = new Date(`${values.date}T${values.end}`);
      // An end earlier in the day means it runs past midnight.
      if (endsAt <= startsAt) endsAt = new Date(endsAt.getTime() + 24 * 60 * 60_000);
    }
    const input = {
      name: values.name,
      description: values.description,
      startsAt: Number.isNaN(startsAt.getTime()) ? '' : startsAt.toISOString(),
      endsAt: endsAt?.toISOString() ?? null,
      channelId: values.where === 'channel' ? values.channelId || null : null,
      location: values.where === 'elsewhere' ? values.location : null,
    };
    const { data, fields: invalid } = validate(createEventSchema, input);
    if (!data) return setFields(invalid);
    const saved = await run(() => (event ? eventApi.update(serverId, event.id, data) : eventApi.create(serverId, data)));
    if (!saved) return;
    useEvents.getState().upsert(saved);
    toast.success(event ? 'Event updated.' : 'Event created.');
    onDone();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={event ? 'Edit event' : 'Create an event'}
      footer={
        <>
          <Button variant="ghost" onClick={onDone}>
            Back
          </Button>
          <Button type="submit" form="event-form" loading={loading}>
            {event ? 'Save' : 'Create event'}
          </Button>
        </>
      }
    >
      <form id="event-form" onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
        {error && <p className="text-sm text-danger">{error}</p>}
        <Input label="Name" data-autofocus maxLength={LIMITS.eventNameMax} value={values.name} onChange={(e) => set({ name: e.target.value })} error={fields.name} placeholder="Game night" />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Date" type="date" value={values.date} onChange={(e) => set({ date: e.target.value })} error={fields.startsAt} />
          <Input label="Starts at" type="time" value={values.start} onChange={(e) => set({ start: e.target.value })} />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={values.hasEnd} onChange={(e) => set({ hasEnd: e.target.checked })} className="accent-[var(--clanfire-accent)]" />
          Add an end time
        </label>
        {values.hasEnd && <Input label="Ends at" type="time" value={values.end} onChange={(e) => set({ end: e.target.value })} error={fields.endsAt} />}
        <fieldset>
          <legend className="mb-2 pl-1 text-[13px] font-semibold text-fg-soft">Where</legend>
          <div className="grid grid-cols-2 gap-2">
            {(['channel', 'elsewhere'] as const).map((where) => (
              <label
                key={where}
                className={cn(
                  'flex h-10 cursor-pointer items-center justify-center gap-1.5 rounded-control border text-sm font-semibold transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/40',
                  values.where === where ? 'border-accent bg-accent-soft text-fg' : 'border-line text-fg-soft hover:bg-hover',
                )}
              >
                <input type="radio" name="where" checked={values.where === where} onChange={() => set({ where })} className="sr-only" />
                {where === 'channel' ? <Volume2 size={15} /> : <MapPin size={15} />}
                {where === 'channel' ? 'In a channel' : 'Somewhere else'}
              </label>
            ))}
          </div>
        </fieldset>
        {values.where === 'channel' ? (
          <Select label="Channel" value={values.channelId} onChange={(e) => set({ channelId: e.target.value })} error={fields.channelId}>
            {channels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.type === 'VOICE' ? '🔊' : '#'} {c.name}
              </option>
            ))}
          </Select>
        ) : (
          <Input label="Location" maxLength={LIMITS.eventLocationMax} value={values.location} onChange={(e) => set({ location: e.target.value })} error={fields.location} placeholder="A place, or a link" />
        )}
        <TextArea
          label="Description"
          rows={3}
          maxLength={LIMITS.eventDescriptionMax}
          value={values.description}
          onChange={(e) => set({ description: e.target.value })}
          error={fields.description}
          placeholder="What's happening? (optional)"
        />
      </form>
    </Modal>
  );
}
